import React, { useState } from "react";
import { Button, Grid, ThemeProvider } from "@mui/material";
import { SETable } from "../../../../components/SETable";
import {
  RiAddFill,
  RiErrorWarningFill,
  RiFilter3Line,
  RiFilterOffLine,
} from "react-icons/ri";
import { SEText } from "../../../../components/SEText";
import { SEButtonExport } from "../../../../components/SEButtonExport";
import useFilterHandler from "../../../../hooks/useFilterHandler";
import {
  removeEmpty,
  removeEmptyProperties,
  sortArrayByProp,
} from "../../../../utils/utils";
import moment from "moment/moment";
import { formatDateToBR } from "../../../../../utils/time";
import useExcelExport from "../../../../hooks/useExcelExport";
import { useQuery } from "react-query";
import { tableColumns } from "./tableColumns";
import SEFilterSelect from "../../../../components/SEFilterSelect";
import theme from "../../../../mui-theme";
import { ArquivosAnexosService } from "../services";
import { useHistory } from "react-router-dom";
import useRenderFormStore from "../../stores/useRenderFormStore";
import useFormMutation from "../hooks/useFormMutation";
import { BaSeDialogContext } from "@base/react";
import { handleModalAdmin } from "../../../../../utils/handleModalAdmin";
import { ArquivoAnexoInterface } from "../services/interfaces";
import SEInputSearch from "../../../../components/SEInputSearch";

const TableArquivosAnexos = () => {
  const history = useHistory();

  const { setFormData, handleShowForm } = useRenderFormStore();

  const [searchText, setSearchText] = useState<any>(null);

  const {
    filterOptions,
    setFilterOptions,
    paginationFilters,
    bodyFilters,
    handleChangeBodyFilter,
    handleResetBodyFilters,
    handleLinesPerPage,
    handleChangeOrder,
    handlePageChange,
    handleSearchChange,
  } = useFilterHandler();

  const { isLoading: isLoadingGetOptions } = useQuery({
    queryFn: () => ArquivosAnexosService.getOptions(),
    queryKey: ["getOptionsArquivosAnexos"],
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    onSuccess: setFilterOptions,
  });

  const { showModal } = React.useContext(BaSeDialogContext);

  const handleCleanFilters = () => {
    handleResetBodyFilters();
    setSearchText(null);
  };

  const { data, isLoading, refetch } = useQuery({
    queryFn: () =>
      ArquivosAnexosService.findAll(paginationFilters, bodyFilters),
    queryKey: ["ArquivosAnexosData", paginationFilters, bodyFilters],
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const { deleteMutation, updateStatusMutation } = useFormMutation({
    showModal,
    service: ArquivosAnexosService,
    onSuccess: () => refetch(),
  });

  const handleDelete = async (id: any) => {
    handleModalAdmin({
      title: `Excluir Arquivo anexo`,
      content: `Você tem certeza que deseja excluir esse arquivo anexo?`,
      msgOnSuccess: `Arquivo anexo excluído com sucesso`,
      msgOnError:
        "O arquivo anexo não foi excluído. Aconteceu um erro ou o sistema está temporariamente fora do ar. Tente novamente mais tarde.",
      onFinish: () => refetch(),
      onConfirmClick: () => deleteMutation(id),
      onCancelClick: () => console.log("modal closed..."),
      showModal: showModal,
    });
  };

  const handleUpdateStatus = async (item: ArquivoAnexoInterface) => {
    const title = `${
      item.status === "APROVADO" ? "Desaprovar" : "Aprovar"
    } esse arquivo anexo`;
    const content = `Você tem certeza que deseja ${
      item.status === "APROVADO" ? "Desaprovar" : "Aprovar"
    } esse arquivo anexo?`;
    const msgOnSuccess = `O arquivo anexo foi ${
      item.status === "APROVADO" ? "Desaprovado" : "Aprovado"
    } com sucesso`;
    const msgOnError = `O arquivo anexo não foi ${
      item.status === "APROVADO" ? "Desaprovado" : "Aprovado"
    } Aconteceu um erro ou o sistema está temporariamente fora do ar. Tente novamente mais tarde.`;
    const novoStatus =
      item.status === "PENDENTE" || item.status === "REPROVADO"
        ? "APROVADO"
        : "REPROVADO";

    const updateStatus = async (item: ArquivoAnexoInterface, status: any) => {
      const dataForm = {
        id: item.id,
        status,
      };
      await updateStatusMutation(removeEmpty(dataForm));
    };

    handleModalAdmin({
      title,
      content,
      msgOnSuccess,
      msgOnError,
      onFinish: () => refetch(),
      onConfirmClick: () => updateStatus(item, novoStatus),
      onCancelClick: () => console.log("modal closed..."),
      showModal: showModal,
    });
  };

  const handleCreate = () => {
    const isCreating = history.location.pathname.includes("/create");
    if (!isCreating) {
      setFormData(null);
      handleShowForm(true);
      history.push(history.location.pathname + "/create");
    }
  };

  const handleEdit = (item: ArquivoAnexoInterface) => {
    setFormData(item);
    history.push(`${history.location.pathname}/${item.id}/edit`);
  };

  const fetchDataExcel = async () => {
    const bodyParams = removeEmptyProperties(bodyFilters);
    const pageFilter = { ...paginationFilters, page: 0, linesPerPage: 1000 };
    const response = await ArquivosAnexosService.findAll(
      pageFilter,
      bodyParams
    );
    return response?.content?.map((xls: any) => ({
      DATA: moment(xls?.dataCriacao).format("DD/MM/YYYY"),
      UF: xls?.uf?.sigla,
      NOME: xls?.nome,
      DESCRICAO: xls?.descricao,
      OBRIGATORIEDADE: xls?.obrigatoriedade,
      STATUS: xls?.status,
      TEMA: xls?.tema.descricao,
    }));
  };

  const { executeDownload, downloading } = useExcelExport({
    handleDataCallback: fetchDataExcel,
    filename: `Exportacao_Arquivos_Anexos_${formatDateToBR(new Date())}`,
    colorLoading: "#3498db",
    isCsv: false,
  });

  const pendingStatus = data?.content?.filter(
    (i: any) => i.status === "PENDENTE"
  ).length;

  const optionsMap: Record<string, string> = {
    SIM: "Sim",
    NAO: "Não",
    SOMENTE_PRIMEIRA_VISITA: "Somente primeira visita",
    SOMENTE_SEGUNDA_VISITA: "Somente segunda visita",
  };

  interface Option {
    value: string;
    label: string;
  }

  const formatRequirements = (values: string[]): Option[] => {
    if (!values) return [];

    return values
      .filter((value) => optionsMap.hasOwnProperty(value))
      .map((value) => ({
        value,
        label: optionsMap[value],
      }));
  };

  return (
    <Grid item>
      <Grid container>
        <Grid item xs={12}>
          <Grid item>
            <Grid item flexDirection="row" display="flex" alignItems="center">
              <Grid
                item
                flexDirection="row"
                display="flex"
                alignItems="center"
                mr={3}
                mb={2}
              >
                <RiFilter3Line />
                <SEText as="p" size={"medium"} font="Mulish" weight="bold">
                  Filtros
                </SEText>
              </Grid>
            </Grid>
            <ThemeProvider theme={theme}>
              <Grid container spacing={2} my={2}>
                <Grid item lg={3} md={2}>
                  <SEFilterSelect
                    defaultOption={"Todas"}
                    id={"uf"}
                    placeholder={"Selecione uma das opções"}
                    label="UF"
                    value={bodyFilters?.uf}
                    options={sortArrayByProp(filterOptions?.ufs, "sigla")}
                    displayField="sigla"
                    onChange={(value) => handleChangeBodyFilter("uf", value)}
                  />
                </Grid>

                <Grid item lg={3} md={2}>
                  <SEFilterSelect
                    defaultOption={"Todas"}
                    placeholder={"Selecione uma das opções"}
                    id={"tema"}
                    label="Tema"
                    displayField="descricao"
                    value={bodyFilters?.tema}
                    options={sortArrayByProp(filterOptions?.temas, "descricao")}
                    onChange={(value) => handleChangeBodyFilter("tema", value)}
                  />
                </Grid>

                <Grid item lg={3} md={2}>
                  <SEFilterSelect
                    defaultOption={"Todas"}
                    placeholder={"Selecione uma das opções"}
                    id={"obrigatoriedade"}
                    label="Obrigatoriedade"
                    displayField="label"
                    valueField="value"
                    value={bodyFilters?.obrigatoriedade}
                    options={formatRequirements(
                      filterOptions?.obrigatoriedades
                    )}
                    onChange={(value) =>
                      handleChangeBodyFilter("obrigatoriedade", value)
                    }
                  />
                </Grid>

                <Grid item lg={3} md={2}>
                  <SEFilterSelect
                    defaultOption={"Todos"}
                    placeholder={"Selecione uma das opções"}
                    id={"tipoAtendente"}
                    label="Perfil atendente"
                    value={bodyFilters?.tipoAtendente}
                    options={filterOptions?.tipoAtendente}
                    onChange={(value) =>
                      handleChangeBodyFilter("tipoAtendente", value)
                    }
                  />
                </Grid>
              </Grid>

              <Grid container justifyContent={"end"} mb={2}>
                <Grid
                  item
                  flexDirection="row"
                  display="flex"
                  alignItems="center"
                >
                  <Button
                    size={"small"}
                    onClick={() => handleCleanFilters()}
                    variant="outlined"
                    startIcon={<RiFilterOffLine />}
                  >
                    <SEText as="p" size={"small"} font="Mulish" weight="bold">
                      Limpar filtros
                    </SEText>
                  </Button>
                </Grid>
              </Grid>
            </ThemeProvider>
          </Grid>
        </Grid>

        <Grid item md={12}>
          <SETable
            pagination={true}
            headerTableColor={"#7B80FF"}
            tableColor={"#FFFFFF"}
            totalElements={data?.totalElements}
            totalPages={data?.totalPages}
            linesPerPageFilters={paginationFilters?.linesPerPage}
            isLoading={isLoading ?? isLoadingGetOptions}
            data={data?.content ?? []}
            page={paginationFilters?.page}
            columns={tableColumns({
              handleEdit,
              handleDelete,
              handleUpdateStatus,
            })}
            orderBy={paginationFilters?.orderBy}
            direction={paginationFilters?.direction}
            onOrderChange={handleChangeOrder}
            onPagePreviousChange={handlePageChange}
            onPageNextChange={handlePageChange}
            onLinesPerPageChange={handleLinesPerPage}
            tableHeader={
              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                mb={2}
              >
                <Grid item md={3}>
                  <SEInputSearch
                    value={searchText}
                    onClick={() => handleSearchChange(searchText)}
                    onChange={(e) => setSearchText(e?.target?.value)}
                  />
                </Grid>

                <Grid
                  item
                  md={8}
                  justifyContent="flex-end"
                  alignItems="center"
                  display={"flex"}
                  flexDirection={"row"}
                >
                  <Grid item mr={2}>
                    <Button
                      style={{
                        backgroundColor: "#7B80FF",
                        fontFamily: "Mulish, sans-serif",
                        fontSize: "12px",
                      }}
                      variant="contained"
                      startIcon={<RiAddFill />}
                      onClick={() => handleCreate()}
                    >
                      Incluir
                    </Button>
                  </Grid>

                  {!!pendingStatus && (
                    <Grid item mr={2}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <>
                          <RiErrorWarningFill color={"#F6E566"} size={26} />
                          {pendingStatus === 1 ? "Existe" : "Existem"}{" "}
                          {pendingStatus}{" "}
                          {pendingStatus === 1 ? "item" : "itens"} aguardando
                          aprovação
                        </>
                      </div>
                    </Grid>
                  )}

                  <Grid item mr={2}>
                    {!!data?.totalElements && (
                      <SEText as="p" font="Mulish">
                        Mostrando <strong>{data?.totalElements}</strong>{" "}
                        {data?.totalElements === 1 ? "resultado" : "resultados"}
                      </SEText>
                    )}
                  </Grid>

                  <Grid item>
                    <SEButtonExport
                      isLoading={downloading}
                      onClick={executeDownload}
                    />
                  </Grid>
                </Grid>
              </Grid>
            }
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default TableArquivosAnexos;
